import { useState, useEffect, useRef, memo, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { MoveHorizontal, ArrowLeftRight, ArrowRight, Files, Trash2, Plus, Settings, Check, ChevronsUpDown, Lock, ChevronDown, ChevronRight, FolderOpen, Folder } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/budget-utils';
import type { CostBudget, CostCategory, ResourceBudget, RoleBudget } from '@/types/budget.types';
import { COST_CATEGORY_TYPE_ORDER, COST_CATEGORY_TYPE_LABELS } from '@/types/budget.types';
import type { GlobalCategory } from '@/types/category.types';
import { fetchGlobalCategories, getDataMode, upsertCostCentreBudget, fetchCostCentreBudgetsByProject } from '@/lib/data-service';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FillSheet } from './fill-sheet';
import { ShiftBudgetSheet } from './shift-budget-sheet';
import { AISuggestion } from './ai-suggestion';
import type { FillType } from './fill-dialog';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';

// ── localStorage helpers for Cost Centre mode budget values ─────────────────
// NOTE: In live mode we skip localStorage entirely so that all users share the
// same API-backed data.  localStorage is only used in demo/tour mode.
const CC_BUDGET_STORAGE_KEY = (projectId: string, ccCode: string) =>
  `cc-cost-budget-${projectId}-${ccCode}`;

interface CcCategoryBudget {
  name: string;
  type: string;
  monthlyBudgets: Record<string, number>; // "YYYY-MM" → value
}

function loadCcBudget(projectId: string, ccCode: string): CcCategoryBudget[] {
  if (getDataMode() === 'live') return [];
  try {
    const raw = localStorage.getItem(CC_BUDGET_STORAGE_KEY(projectId, ccCode));
    if (raw) return JSON.parse(raw) as CcCategoryBudget[];
  } catch { /* ignore */ }
  return [];
}

function saveCcBudget(projectId: string, ccCode: string, rows: CcCategoryBudget[]) {
  if (getDataMode() === 'live') return;
  try {
    localStorage.setItem(CC_BUDGET_STORAGE_KEY(projectId, ccCode), JSON.stringify(rows));
  } catch { /* ignore */ }
}

// Default category names used per cost centre section in Cost Centre mode
const DEFAULT_CC_CATEGORIES = [
  { name: 'Labour', type: 'labour' },
  { name: 'Materials', type: 'expenses' },
  { name: 'Equipment', type: 'expenses' },
  { name: 'Subcontractors', type: 'subcontractors' },
  { name: 'Overhead', type: 'overhead' },
];

// ── Cost Centre mode: buildCbTree / flattenCbHierarchy ───────────────────────
// (Pre-emptive Issue 4 fix — always call buildCbTree before flattenCbHierarchy)

interface FlatCbNode extends HierarchicalCostCentre {
  level: number;
  hasChildren: boolean;
}

/** Build a proper nested tree from a flat array with parentId references */
function buildCbTree(items: HierarchicalCostCentre[]): HierarchicalCostCentre[] {
  const map = new Map<number, HierarchicalCostCentre>();
  const roots: HierarchicalCostCentre[] = [];

  items.forEach(item => {
    map.set(item.id, { ...item, children: [], level: 0 });
  });

  items.forEach(item => {
    const node = map.get(item.id)!;
    if (item.parentId !== undefined && item.parentId !== null && map.has(item.parentId)) {
      const parent = map.get(item.parentId)!;
      if (!parent.children) parent.children = [];
      parent.children.push(node);
      node.level = (parent.level || 0) + 1;
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/** Depth-first flatten of a nested tree, adding level and hasChildren metadata */
function flattenCbHierarchy(nodes: HierarchicalCostCentre[], level = 0): FlatCbNode[] {
  const result: FlatCbNode[] = [];
  for (const node of nodes) {
    result.push({
      ...node,
      level,
      hasChildren: !!(node.children && node.children.length > 0),
    });
    if (node.children && node.children.length > 0) {
      result.push(...flattenCbHierarchy(node.children, level + 1));
    }
  }
  return result;
}

interface EditCostBudgetFormProps {
  costBudget: CostBudget;
  onChange: (changes: CostBudget) => void;
  /** Fires when a cell edit is committed (on blur), signalling the page to snapshot undo history */
  onCellCommit?: () => void;
  selectedYear?: string;
  projectStartDate?: string;
  projectEndDate?: string;
  availableCategories: CostCategory[];
  resourceBudget?: ResourceBudget; // Used to auto-calculate Labour category
  hasResourceBudget?: boolean; // When true: Labour row is locked (read-only). When false: Labour row is editable.
  cascadeTargetCategoryId?: number | null; // The designated cascade target category from Budget Settings
  // Cost Centre mode props
  isCostCentreMode?: boolean;
  costCentres?: CostCentre[];
  hierarchicalCostCentres?: HierarchicalCostCentre[];
  isStructureView?: boolean;
  projectId?: string;
}

// Month names for columns
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Mock data: Role rates (£/hour)
// In production, this would come from the backend API
const ROLE_RATES: Record<string, number> = {
  'Project Manager': 200,
  'Senior Consultant': 180,
  'Consultant': 150,
  'Developer': 150,
  'Senior Developer': 175,
  'QA Analyst': 125,
  'QA Engineer': 125,
  'Business Analyst': 140,
  'Technical Architect': 220,
  'UX Designer': 160,
  'Scrum Master': 175,
};

// Mock data: Resource Budget (hours per role per month)
// In production, this would come from the Resource Budget API
interface MockResourceBudget {
  role: string;
  rate: number;
  monthlyHours: Record<string, number>; // monthKey -> hours
}

const MOCK_RESOURCE_BUDGET: MockResourceBudget[] = [
  {
    role: 'Project Manager',
    rate: ROLE_RATES['Project Manager'],
    monthlyHours: {
      '2024-05': 80,
      '2024-06': 80,
      '2024-07': 80,
      '2024-08': 80,
      '2024-09': 80,
      '2024-10': 80,
      '2024-11': 80,
      '2024-12': 80,
      '2025-01': 80,
    },
  },
  {
    role: 'Senior Developer',
    rate: ROLE_RATES['Senior Developer'],
    monthlyHours: {
      '2024-05': 160,
      '2024-06': 160,
      '2024-07': 160,
      '2024-08': 160,
      '2024-09': 160,
      '2024-10': 160,
      '2024-11': 160,
      '2024-12': 160,
      '2025-01': 160,
    },
  },
  {
    role: 'Developer',
    rate: ROLE_RATES['Developer'],
    monthlyHours: {
      '2024-05': 160,
      '2024-06': 160,
      '2024-07': 160,
      '2024-08': 160,
      '2024-09': 160,
      '2024-10': 160,
      '2024-11': 160,
      '2024-12': 160,
      '2025-01': 160,
    },
  },
  {
    role: 'QA Engineer',
    rate: ROLE_RATES['QA Engineer'],
    monthlyHours: {
      '2024-05': 80,
      '2024-06': 80,
      '2024-07': 80,
      '2024-08': 80,
      '2024-09': 80,
      '2024-10': 80,
      '2024-11': 80,
      '2024-12': 80,
      '2025-01': 80,
    },
  },
];

// Helper function to calculate Labour budget from Resource Budget
// Labour = Sum of (hours × rate) for all roles in that month
const calculateLabourBudget = (monthKey: string): number => {
  return MOCK_RESOURCE_BUDGET.reduce((total, resource) => {
    const hours = resource.monthlyHours[monthKey] || 0;
    return total + (hours * resource.rate);
  }, 0);
};

// Mock data: Initial budget values for non-Labour categories
const MOCK_INITIAL_BUDGETS: Record<string, Record<string, number>> = {
  'Materials': {
    '2024-05': 5000,
    '2024-06': 3000,
    '2024-07': 2000,
    '2024-08': 2000,
    '2024-09': 1500,
    '2024-10': 1500,
    '2024-11': 1000,
    '2024-12': 1000,
    '2025-01': 500,
  },
  'Equipment': {
    '2024-05': 15000,
    '2024-06': 2000,
    '2024-07': 1000,
    '2024-08': 1000,
    '2024-09': 1000,
    '2024-10': 1000,
    '2024-11': 500,
    '2024-12': 500,
    '2025-01': 500,
  },
  'Subcontractors': {
    '2024-05': 0,
    '2024-06': 0,
    '2024-07': 10000,
    '2024-08': 10000,
    '2024-09': 10000,
    '2024-10': 10000,
    '2024-11': 0,
    '2024-12': 0,
    '2025-01': 0,
  },
  'Travel & Expenses': {
    '2024-05': 1500,
    '2024-06': 1500,
    '2024-07': 1500,
    '2024-08': 1500,
    '2024-09': 1500,
    '2024-10': 1500,
    '2024-11': 1500,
    '2024-12': 1500,
    '2025-01': 1500,
  },
  'Software & Licenses': {
    '2024-05': 5000,
    '2024-06': 500,
    '2024-07': 500,
    '2024-08': 500,
    '2024-09': 500,
    '2024-10': 500,
    '2024-11': 500,
    '2024-12': 500,
    '2025-01': 500,
  },
  'Overhead': {
    '2024-05': 3000,
    '2024-06': 3000,
    '2024-07': 3000,
    '2024-08': 3000,
    '2024-09': 3000,
    '2024-10': 3000,
    '2024-11': 3000,
    '2024-12': 3000,
    '2025-01': 3000,
  },
  'Contingency': {
    '2024-05': 5000,
    '2024-06': 5000,
    '2024-07': 5000,
    '2024-08': 5000,
    '2024-09': 5000,
    '2024-10': 5000,
    '2024-11': 5000,
    '2024-12': 5000,
    '2025-01': 5000,
  },
};

// NumberInput component that handles 2dp formatting properly with max value validation
const NumberInput = ({ value, onChange, onCommit, maxValue = 999999.99, onFocus, disabled, ...props }: { value: number; onChange: (value: number) => void; onCommit?: () => void; maxValue?: number; onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void; disabled?: boolean; [key: string]: any }) => {
  const [displayValue, setDisplayValue] = useState(value.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Value at the moment the cell gained focus — used to detect real edits on blur
  // so we don't record spurious undo/redo history entries when a cell is focused
  // and blurred without changing its value.
  const valueOnFocusRef = useRef(value);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    setIsFocused(true);
    valueOnFocusRef.current = value;
    // Select all text on focus for easy editing
    e.target.select();
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    let numValue = parseFloat(displayValue) || 0;
    
    // Enforce maximum value
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    const formattedValue = parseFloat(numValue.toFixed(2));
    setDisplayValue(formattedValue.toFixed(2));
    // Only call onChange if the value differs from the current prop.
    if (formattedValue !== value) {
      onChange(formattedValue);
    }
    // Only commit (snapshot undo/redo history) when the value actually changed
    // since focus, so focusing/blurring a cell without editing it does not
    // increment the Undo/Redo count. Compared against the value at focus time
    // because typing fires a debounced onChange that updates the `value` prop.
    if (formattedValue !== valueOnFocusRef.current) {
      onCommit?.();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const val = e.target.value;
    // Allow typing numbers, decimal point, and empty string
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      
      // Debounced onChange to enable toolbar buttons while typing
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new debounced timer (300ms delay to allow for slow typing)
      debounceTimerRef.current = setTimeout(() => {
        let numValue = parseFloat(val) || 0;
        // Enforce maximum value
        if (numValue > maxValue) {
          numValue = maxValue;
        }
        const formattedValue = parseFloat(numValue.toFixed(2));
        // Only call onChange if the value actually changed
        if (formattedValue !== value) {
          onChange(formattedValue);
        }
      }, 300);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
};

// Helper function to format numbers with thousand separators
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Extended category interface with monthly budgets as array for form display
interface CategoryBudgetRow extends Omit<CostCategory, 'monthlyBudgets'> {
  monthlyBudgets: number[]; // Budget per month (array for form display, converted to/from Record for API)
}

export function EditCostBudgetForm({
  costBudget,
  onChange,
  onCellCommit,
  selectedYear,
  projectStartDate,
  projectEndDate,
  availableCategories,
  resourceBudget,
  hasResourceBudget = false,
  cascadeTargetCategoryId,
  isCostCentreMode = false,
  costCentres = [],
  hierarchicalCostCentres = [],
  isStructureView = false,
  projectId,
}: EditCostBudgetFormProps) {
  // Calculate all available months for the project across all years
  const allProjectMonths = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Show all months of current year if no project dates
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => ({ year: currentYear, month: i }));
    }
    
    const startDate = new Date(projectStartDate);
    const endDate = new Date(projectEndDate);
    
    const months: { year: number; month: number }[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      months.push({ year: current.getFullYear(), month: current.getMonth() });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  })();

  // Show ALL months for the entire project duration - no year filtering
  const yearMonths = allProjectMonths;

  // Debug logging
  console.log('[EditCostBudgetForm] Project dates:', { projectStartDate, projectEndDate });
  console.log('[EditCostBudgetForm] Total months to display:', yearMonths.length);
  console.log('[EditCostBudgetForm] Month range:', yearMonths.length > 0 ? `${MONTHS[yearMonths[0].month]} ${yearMonths[0].year} - ${MONTHS[yearMonths[yearMonths.length - 1].month]} ${yearMonths[yearMonths.length - 1].year}` : 'None');

  // Create a mapping from global month index to year/month
  const monthIndexMap = yearMonths.map((ym, index) => ({
    globalIndex: index,
    year: ym.year,
    month: ym.month,
    yearMonthKey: `${ym.year}-${ym.month}` // Unique key for this month
  }));

  // Show ALL months - no dynamic loading
  const visibleMonths = monthIndexMap.map(m => m.globalIndex);
  
  // Total number of months to display
  const totalMonths = yearMonths.length;

  // Check if we're in Live mode - don't use mock data in Live mode
  const isLiveMode = getDataMode() === 'live';

  // Derive the effective cost rate for a role. The backend RoleBudget entity does
  // not persist a separate CostRate column, so we derive it from budgetCost/budgetHours
  // (which IS stored as hours × costRate during cascade). Falls back to billRate only
  // when budgetCost is zero (e.g. newly created role with no cost data yet).
  const getEffectiveCostRate = (role: RoleBudget): number => {
    if (role.costRate != null) return role.costRate;
    if (role.budgetHours > 0 && role.budgetCost > 0) return role.budgetCost / role.budgetHours;
    return role.billRate ?? 0;
  };

  // Helper function to calculate Labour budget from actual Resource Budget data
  // Labour = Sum of (hours × costRate) for all roles in that month
  const calculateLabourFromResourceBudget = (monthKey: string): number => {
    if (!resourceBudget?.byRole || resourceBudget.byRole.length === 0) {
      return 0;
    }
    
    return resourceBudget.byRole.reduce((total, role) => {
      // monthKey is already in "YYYY-MM" format — use directly as lookup key
      const hours = role.monthlyHours?.[monthKey] || 0;
      const rate = getEffectiveCostRate(role);
      
      return total + (hours * rate);
    }, 0);
  };

  const buildCategoryRows = useCallback((cats: CostCategory[]): CategoryBudgetRow[] => {
    return cats.map(category => {
      // Create monthly budgets array
      const monthlyBudgets = monthIndexMap.map(({ year, month }) => {
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
        
        // First priority: Use saved monthly budgets from API if available
        if (category.monthlyBudgets && category.monthlyBudgets[monthKey] !== undefined) {
          return category.monthlyBudgets[monthKey];
        }
        
        // In Live mode: For Labour, calculate from actual Resource Budget; for others, return 0
        if (isLiveMode) {
          if (category.name === 'Labour' || category.type === 'labour') {
            return calculateLabourFromResourceBudget(monthKey);
          }
          return 0;
        }
        
        // Demo mode only: Labour category - Calculate from mock Resource Budget (hours × rate)
        if (category.name === 'Labour') {
          return calculateLabourBudget(monthKey);
        }
        
        // Demo mode only: Use mock initial budgets as fallback for new categories
        const categoryBudgets = MOCK_INITIAL_BUDGETS[category.name];
        return categoryBudgets?.[monthKey] || 0;
      });

      return {
        ...category,
        monthlyBudgets,
      };
    });
  }, [calculateLabourFromResourceBudget, isLiveMode, monthIndexMap]);

  // Initialize categories with properly sized monthlyBudgets arrays
  // In Live mode: Only use saved data from API, no mock fallbacks
  // In Demo mode: Use mock data as fallback for demonstration purposes
  const [categories, setCategories] = useState<CategoryBudgetRow[]>(() => buildCategoryRows(availableCategories));

  // Allow availableCategories to arrive async (e.g. fetched default GlobalCategories)
  useEffect(() => {
    if (categories.length === 0 && availableCategories.length > 0) {
      setCategories(buildCategoryRows(availableCategories));
    }
  }, [availableCategories, buildCategoryRows, categories.length]);

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedCell, setSelectedCell] = useState<{
    categoryIndex: number;
    monthIndex: number | null;
  } | null>(null);

  // Track which Labour category is expanded to show role breakdown
  const [expandedLabourCategory, setExpandedLabourCategory] = useState<number | null>(null);

  // Calculate labour cost per month from resource budget (hours × costRate per role per month)
  const calculateLabourCostFromResourceBudget = (): number[] => {
    if (!resourceBudget?.byRole) {
      return Array(totalMonths).fill(0);
    }

    const monthlyCost = Array(totalMonths).fill(0);

    yearMonths.forEach((yearMonth, projectMonthIndex) => {
      const monthKey = `${yearMonth.year}-${String(yearMonth.month + 1).padStart(2, '0')}`;
      resourceBudget.byRole.forEach((role: RoleBudget) => {
        const hours = role.monthlyHours?.[monthKey] || 0;
        monthlyCost[projectMonthIndex] += hours * getEffectiveCostRate(role);
      });
    });

    return monthlyCost;
  };

  // Get roles that have hours allocated in any month
  const getRolesWithHours = (): RoleBudget[] => {
    if (!resourceBudget?.byRole) return [];
    return resourceBudget.byRole.filter((role: RoleBudget) => {
      return role.monthlyHours && Object.values(role.monthlyHours).some(h => h > 0);
    });
  };

  // Fill dialog state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ categoryIndex: number; monthIndex: number; value: number } | null>(null);

  // Shift budget dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ categoryIndex: number; categoryName: string } | null>(null);

  // Available global categories for inline combobox
  const [availableGlobalCategories, setAvailableGlobalCategories] = useState<GlobalCategory[]>([]);
  const [openCategoryPopover, setOpenCategoryPopover] = useState<number | null>(null);

  // Delete confirmation state
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; category: CategoryBudgetRow } | null>(null);

  // AI suggestion state for pattern detection
  const [patternSuggestion, setPatternSuggestion] = useState<{
    categoryIndex: number;
    value: number;
    fromMonth: number;
    toMonth: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Track if this is the initial mount to prevent marking form as dirty on load
  const isInitialMount = useRef(true);

  const navigate = useNavigate();

  // Track whether any global categories exist (for empty state messaging)
  const [hasGlobalCategories, setHasGlobalCategories] = useState<boolean | null>(null);

  const goToBudgetSettings = useCallback(() => {
    navigate('/settings/general?tab=budget-settings');
  }, [navigate]);

  // Load global categories on mount for inline combobox
  useEffect(() => {
    fetchGlobalCategories().then(globalCategories => {
      const activeCategories = globalCategories.filter(gc => gc.status === 'active');
      setAvailableGlobalCategories(activeCategories);
      setHasGlobalCategories(activeCategories.length > 0);
    }).catch(() => {
      setHasGlobalCategories(false);
    });
  }, []);

  // Auto-inject the cascade target category when a Resource Budget exists and no matching category is present.
  // This ensures the synthetic Labour group row is always visible on first load (empty-state path).
  useEffect(() => {
    if (!hasResourceBudget || !cascadeTargetCategoryId) return;
    const hasTarget = categories.some(
      c => c.id === cascadeTargetCategoryId || c.globalCategoryId === cascadeTargetCategoryId
    );
    if (hasTarget) return;

    const targetGlobal = availableGlobalCategories.find(gc => gc.id === cascadeTargetCategoryId);
    if (!targetGlobal) return;

    const targetCategory: CategoryBudgetRow = {
      id: targetGlobal.id,
      name: targetGlobal.name,
      type: targetGlobal.type,
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'project',
      isCustom: false,
      globalCategoryId: targetGlobal.id,
      monthlyBudgets: Array(totalMonths).fill(0),
    };
    setCategories(prev => [targetCategory, ...prev]);
  }, [hasResourceBudget, cascadeTargetCategoryId, availableGlobalCategories, totalMonths]);

  useEffect(() => {
    // Skip onChange on initial mount to prevent false "unsaved changes" alert
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Calculate totals for each category and convert monthlyBudgets array to Record<string, number>
    const updatedCategories = categories.map(cat => {
      // Convert monthlyBudgets array to Record<string, number> for API storage
      const monthlyBudgetsRecord: Record<string, number> = {};
      cat.monthlyBudgets.forEach((value, index) => {
        if (monthIndexMap[index]) {
          const { year, month } = monthIndexMap[index];
          const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
          monthlyBudgetsRecord[monthKey] = value;
        }
      });

      return {
        ...cat,
        budget: cat.monthlyBudgets.reduce((sum, val) => sum + val, 0),
        monthlyBudgets: monthlyBudgetsRecord,
      };
    });

    // Calculate overall totals
    const totalBudget = updatedCategories.reduce((sum, cat) => sum + cat.budget, 0);
    const totalActual = updatedCategories.reduce((sum, cat) => sum + cat.actual, 0);
    const totalCommitted = updatedCategories.reduce((sum, cat) => sum + cat.committed, 0);

    onChange({
      ...costBudget,
      categories: updatedCategories,
      totals: {
        budget: totalBudget,
        actual: totalActual,
        committed: totalCommitted,
        available: totalBudget - totalActual - totalCommitted,
        progressPercent: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
      },
    });
  }, [categories]);

  const updateMonthlyBudget = (categoryIndex: number, monthIndex: number, value: number) => {
    console.log('[updateMonthlyBudget] Called:', { categoryIndex, monthIndex, value, totalMonths });
    const updated = [...categories];
    console.log('[updateMonthlyBudget] Current category:', updated[categoryIndex]);
    
    // Ensure monthlyBudgets array is properly sized
    const existingBudgets = updated[categoryIndex].monthlyBudgets || [];
    console.log('[updateMonthlyBudget] Existing budgets:', existingBudgets);
    const monthlyBudgets = existingBudgets.length >= totalMonths 
      ? [...existingBudgets]
      : [...existingBudgets, ...Array(totalMonths - existingBudgets.length).fill(0)];
    console.log('[updateMonthlyBudget] Sized monthlyBudgets:', monthlyBudgets);
    
    monthlyBudgets[monthIndex] = value;
    console.log('[updateMonthlyBudget] After update:', monthlyBudgets);
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    
    // Pattern detection for AI suggestions
    detectPattern(categoryIndex, monthIndex, value, monthlyBudgets);
  };

  // Pattern detection for AI suggestions
  const detectPattern = (categoryIndex: number, monthIndex: number, value: number, monthlyBudgets: number[]) => {
    // Don't suggest if already dismissed
    const suggestionKey = `pattern-${categoryIndex}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    
    // Check if last 3 values are the same and non-zero
    if (monthIndex >= 2 && value > 0) {
      const last3 = [
        monthlyBudgets[monthIndex - 2],
        monthlyBudgets[monthIndex - 1],
        monthlyBudgets[monthIndex]
      ];
      
      const allSame = last3.every(v => v === value);
      
      if (allSame && monthIndex < totalMonths - 1) {
        // Show suggestion to fill remaining months
        setPatternSuggestion({
          categoryIndex,
          value,
          fromMonth: monthIndex + 1,
          toMonth: totalMonths - 1
        });
      }
    }
  };

  // Row selection handlers
  const toggleRowSelection = (categoryId: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(categoryId)) {
      newSelection.delete(categoryId);
    } else {
      newSelection.add(categoryId);
    }
    setSelectedRows(newSelection);
    // Clear cell selection when selecting a row
    setSelectedCell(null);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === displayCategories.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(displayCategories.map(cat => cat.id)));
    }
    // Clear cell selection when selecting all rows
    setSelectedCell(null);
  };

  // Fill handlers
  const handleFillClick = (categoryIndex: number, monthIndex: number) => {
    const category = categories[categoryIndex];
    const value = category.monthlyBudgets[monthIndex] || 0;
    setFillSource({ categoryIndex, monthIndex, value });
    setFillDialogOpen(true);
  };

  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;

    const { categoryIndex, monthIndex, value } = fillSource;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];

    // Determine the range based on fill direction
    const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
    const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
    const monthCount = Math.abs(endMonth - startMonth) + 1;

    // Apply fill based on type
    if (fillType === 'copy') {
      // Copy same value to all months in range
      for (let i = startMonth; i <= endMonth; i++) {
        monthlyBudgets[i] = value;
      }
    } else if (fillType === 'distribute') {
      // Distribute value equally across months
      const valuePerMonth = value / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100;
      const remainder = value - (roundedValue * monthCount);
      
      for (let i = startMonth; i <= endMonth; i++) {
        const isLast = i === endMonth;
        monthlyBudgets[i] = isLast ? roundedValue + remainder : roundedValue;
      }
    }

    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };

    setCategories(updated);
    setFillDialogOpen(false);
    setFillSource(null);
    onCellCommit?.();
  };

  // Handle pattern suggestion acceptance
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    
    const { categoryIndex, value, fromMonth, toMonth } = patternSuggestion;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];
    
    // Fill remaining months with the pattern value
    for (let i = fromMonth; i <= toMonth; i++) {
      monthlyBudgets[i] = value;
    }
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    setPatternSuggestion(null);
    onCellCommit?.();
  };
  
  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      const suggestionKey = `pattern-${patternSuggestion.categoryIndex}`;
      setDismissedSuggestions(new Set(dismissedSuggestions).add(suggestionKey));
    }
    setPatternSuggestion(null);
  };

  // Duplicate handlers
  const handleDuplicateCategory = (categoryIndex: number) => {
    const sourceCategory = categories[categoryIndex];
    const newCategory: CategoryBudgetRow = {
      ...sourceCategory,
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: `${sourceCategory.name} (Copy)`,
      monthlyBudgets: [...sourceCategory.monthlyBudgets],
      scope: 'project', // Duplicated categories are project-specific
      isCustom: true,
    };

    // Insert below source category
    const updated = [
      ...categories.slice(0, categoryIndex + 1),
      newCategory,
      ...categories.slice(categoryIndex + 1),
    ];

    setCategories(updated);
  };

  const handleDuplicateMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const updatedCategories = [...categories];

    // Process in reverse order to maintain correct insertion positions
    for (let i = updatedCategories.length - 1; i >= 0; i--) {
      const category = updatedCategories[i];
      if (selectedCategoryIds.includes(category.id)) {
        const newCategory: CategoryBudgetRow = {
          ...category,
          id: Date.now() + Math.floor(Math.random() * 1000) + i,
          name: `${category.name} (Copy)`,
          monthlyBudgets: [...category.monthlyBudgets],
          scope: 'project',
          isCustom: true,
        };
        updatedCategories.splice(i + 1, 0, newCategory);
      }
    }

    setCategories(updatedCategories);
    setSelectedRows(new Set());
  };

  // Shift budget handler
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;

    const categoryIndex = shiftSource.categoryIndex;
    const category = categories[categoryIndex];
    const newMonthlyBudgets = [...(category.monthlyBudgets || Array(totalMonths).fill(0))];

    // Copy values to destination
    const shiftAmount = destinationStart - sourceFrom;
    const sourceMonthlyBudgets = category.monthlyBudgets || Array(totalMonths).fill(0);
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        newMonthlyBudgets[destIndex] = sourceMonthlyBudgets[i];
      }
    }

    // Clear source if requested
    if (clearSource) {
      const destinationEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        if (i < destinationStart || i > destinationEnd) {
          newMonthlyBudgets[i] = 0;
        }
      }
    }

    const updated = [...categories];
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets: newMonthlyBudgets,
    };

    setCategories(updated);
    setShiftDialogOpen(false);
    setShiftSource(null);
    onCellCommit?.();
  };

  // Delete handlers
  const removeCategory = (categoryIndex: number) => {
    const category = categories[categoryIndex];
    
    // Prevent deletion of global categories
    if (category.scope === 'global') {
      setDeleteAttempt({ index: categoryIndex, category });
      return;
    }

    // Allow deletion of project-specific categories
    setCategories(categories.filter((_, i) => i !== categoryIndex));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const removeMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const categoriesToDelete = categories.filter(cat => selectedCategoryIds.includes(cat.id));

    // Check if any selected categories have actual costs
    const categoriesWithCosts = categoriesToDelete.filter(cat => {
      // Check if there are any actual costs in the category
      const hasActualCosts = cat.actual > 0;
      // Also check if there are any monthly budgets allocated
      const hasMonthlyBudgets = cat.monthlyBudgets && cat.monthlyBudgets.some(budget => budget > 0);
      return hasActualCosts || hasMonthlyBudgets;
    });

    if (categoriesWithCosts.length > 0) {
      setDeleteAttempt({ index: -1, category: categoriesWithCosts[0] });
      return;
    }

    // Allow deletion of categories without costs
    setCategories(categories.filter(cat => !selectedCategoryIds.includes(cat.id)));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const cancelDeleteCategory = () => {
    setDeleteAttempt(null);
  };

  // Add Category handler (inline row, matching Resource Budget pattern)
  const addCategory = () => {
    const usedCategoryIds = categories.map(c => c.globalCategoryId).filter(Boolean);
    const availableCategory = availableGlobalCategories.find(
      gc => !usedCategoryIds.includes(gc.id) && gc.id !== cascadeTargetCategoryId
    );

    const newCategory: CategoryBudgetRow = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: availableCategory?.name || '',
      code: availableCategory?.code,
      type: availableCategory?.type || 'expenses',
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'project',
      isCustom: !availableCategory,
      globalCategoryId: availableCategory?.id,
      monthlyBudgets: Array(totalMonths).fill(0),
    };

    setCategories([...categories, newCategory]);
  };

  const updateCategorySelection = (index: number, globalCategoryId: string) => {
    if (!globalCategoryId) return;
    const globalCategory = availableGlobalCategories.find(gc => String(gc.id) === globalCategoryId);
    if (!globalCategory) return;

    const updated = [...categories];
    updated[index] = {
      ...updated[index],
      name: globalCategory.name,
      code: globalCategory.code,
      type: globalCategory.type,
      globalCategoryId: globalCategory.id,
      isCustom: false,
    };
    setCategories(updated);
  };

  // ── Labour group synthesis ───────────────────────────────────────────────
  // When a Resource Budget exists, all labour-type categories are replaced by
  // a single synthetic group whose child rows come from resourceBudget.byRole.
  // The group name comes from the cascade target category when set.
  const LABOUR_SYNTHETIC_ID = -1;
  const isCascadeTarget = (c: CategoryBudgetRow) =>
    cascadeTargetCategoryId != null && (c.id === cascadeTargetCategoryId || c.globalCategoryId === cascadeTargetCategoryId);
  const labourCategories = categories.filter(c => isCascadeTarget(c));
  const nonLabourCategories = categories.filter(c => !isCascadeTarget(c));
  // The rows actually rendered in the table (cascade target replaced by synthetic group)
  const displayCategories = hasResourceBudget ? nonLabourCategories : categories;
  const duplicateCategoryNames = useMemo(() => {
    const names = displayCategories.map(c => c.name).filter(Boolean);
    return new Set(names.filter((name, i) => names.indexOf(name) !== i));
  }, [displayCategories]);
  const showSyntheticLabour = hasResourceBudget && labourCategories.length > 0;
  const isLabourGroupExpanded = expandedLabourCategory === LABOUR_SYNTHETIC_ID;
  // Prefer the stored monthlyBudgets from the cascade target category (authoritative values
  // computed by the backend during cascade using the correct CostRate). Only fall back to
  // recalculating from resource budget roles when no stored monthly data is available.
  const labourGroupMonthly = showSyntheticLabour
    ? (labourCategories[0]?.monthlyBudgets?.some((v: number) => v > 0)
      ? [...labourCategories[0].monthlyBudgets]
      : calculateLabourCostFromResourceBudget())
    : [];
  const labourGroupTotal = labourGroupMonthly.reduce((s, v) => s + v, 0);
  const labourRoles = getRolesWithHours();

  // Resolve the display name for the synthetic Labour group row.
  // Uses the cascade target category name when available, otherwise falls back to "Labour".
  const cascadeTargetDisplayName = (() => {
    if (cascadeTargetCategoryId) {
      const fromCat = labourCategories.find(c => c.id === cascadeTargetCategoryId || c.globalCategoryId === cascadeTargetCategoryId);
      if (fromCat) return fromCat.name;
      const fromGlobal = availableGlobalCategories.find(gc => gc.id === cascadeTargetCategoryId);
      if (fromGlobal) return fromGlobal.name;
    }
    return 'Labour';
  })();

  // ── Cost Centre mode — render per-cost-centre sections ──────────────────
  if (isCostCentreMode) {
    return (
      <CostCentreBudgetView
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        projectId={projectId || ''}
        yearMonths={yearMonths}
        monthIndexMap={monthIndexMap}
        totalMonths={totalMonths}
        onChange={onChange}
        onCellCommit={onCellCommit}
        costBudget={costBudget}
      />
    );
  }

  return (
    <div className="space-y-4 -mx-6">
      <div className="">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
          <h2 className="text-lg font-semibold">Budget by Category</h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const { categoryIndex, monthIndex } = selectedCell;
                  const category = categories[categoryIndex];
                  const value = category.monthlyBudgets?.[monthIndex] || 0;
                  // Validation: value > 0 && totalMonths > 1 (can fill in at least one direction)
                  if (value > 0 && totalMonths > 1) {
                    handleFillClick(categoryIndex, monthIndex);
                  }
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (() => {
                if (!selectedCell || selectedCell.monthIndex === null) return true;
                const { categoryIndex, monthIndex } = selectedCell;
                const category = categories[categoryIndex];
                if (!category) return true;
                const value = category.monthlyBudgets?.[monthIndex] || 0;
                // Disabled if: no value (value === 0) OR only one column (totalMonths === 1)
                return value === 0 || totalMonths === 1;
              })()}
              title="Fill from selected cell"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) handleDuplicateCategory(categoryIndex);
                } else if (selectedRows.size > 1) {
                  handleDuplicateMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Duplicate ${selectedRows.size} selected categories` : "Duplicate category"}
            >
              <Files className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const categoryIndex = selectedCell.categoryIndex;
                  setShiftSource({ 
                    categoryIndex, 
                    categoryName: categories[categoryIndex].name 
                  });
                  setShiftDialogOpen(true);
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (categories[selectedCell.categoryIndex]?.monthlyBudgets?.[selectedCell.monthIndex] ?? 0) === 0}
              title="Shift budget between months (select a monthly cell first)"
            >
              <MoveHorizontal className="h-4 w-4 mr-2" />
              Shift
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) {
                    removeCategory(categoryIndex);
                  }
                } else if (selectedRows.size > 1) {
                  removeMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Delete ${selectedRows.size} selected categories` : "Delete selected category"}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button 
              onClick={addCategory} 
              size="sm" 
              variant="outline"
              disabled={availableGlobalCategories.length > 0 && categories.filter(c => c.globalCategoryId).length >= availableGlobalCategories.length}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
        <div className="p-0">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {hasGlobalCategories === false ? (
                <div className="space-y-2">
                  <p>No budget categories configured.</p>
                  <p>
                    Please configure categories in{' '}
                    <button
                      type="button"
                      onClick={goToBudgetSettings}
                      className="text-primary underline underline-offset-4 hover:text-primary/80 inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Settings → Budget Settings
                    </button>
                  </p>
                </div>
              ) : (
                <>No categories yet. Click "Add Category" to get started.</>
              )}
            </div>
          ) : (
            <>
              {/* 3-Column Layout: Left (sticky) + Middle (scrollable) + Right (sticky) */}
              <div className="flex border rounded-md">
                {/* LEFT SECTION: Checkbox + Category (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-r">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px] sticky left-0 z-10 bg-background">
                          <Checkbox
                            checked={selectedRows.size === displayCategories.length && displayCategories.length > 0}
                            onCheckedChange={toggleAllRows}
                            aria-label="Select all rows"
                          />
                        </TableHead>
                        <TableHead className="w-[200px] sticky left-[40px] z-10 border-r bg-background">Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* ── Synthetic Labour group row (only when Resource Budget exists) ── */}
                      {showSyntheticLabour && (
                        <Fragment key="labour-group">
                          <TableRow className="bg-muted/40">
                            <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/40">
                              <div className="flex items-center h-9">
                                <button
                                  onClick={() => setExpandedLabourCategory(isLabourGroupExpanded ? null : LABOUR_SYNTHETIC_ID)}
                                  className="p-1 hover:bg-muted rounded"
                                  title={isLabourGroupExpanded ? 'Collapse role breakdown' : 'Expand to see role breakdown'}
                                >
                                  {isLabourGroupExpanded
                                    ? <ChevronDown className="h-4 w-4" />
                                    : <ChevronRight className="h-4 w-4" />}
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/40">
                              <div className="flex items-center gap-2 h-9 px-2">
                                <span className="font-medium">{cascadeTargetDisplayName}</span>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[220px]">
                                      <p>{cascadeTargetDisplayName} budget is calculated from the Resource Budget. To change it, revise the Resource Budget.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </TableCell>
                          </TableRow>

                          {/* Role name child rows */}
                          {isLabourGroupExpanded && (
                            labourRoles.length === 0 ? (
                              <TableRow className="bg-muted/10 border-l-2 border-l-primary/30">
                                <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/10" />
                                <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/10">
                                  <span className="text-xs text-muted-foreground italic pl-4">No roles with allocated hours</span>
                                </TableCell>
                              </TableRow>
                            ) : labourRoles.map(role => (
                              <TableRow key={`labour-role-name-${role.id}`} className="bg-muted/10 border-l-2 border-l-primary/30 h-[41px]">
                                <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/10 h-[41px]" />
                                <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/10 h-[41px]">
                                  <Input
                                    value={`↳ ${role.role}`}
                                    readOnly
                                    className="w-full h-9 px-2 text-sm bg-transparent text-muted-foreground cursor-default border-transparent pl-6"
                                    title={role.role}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </Fragment>
                      )}

                      {/* ── Non-labour category rows (or all rows when no Resource Budget) ── */}
                      {displayCategories.map((category) => {
                        const categoryIdx = categories.findIndex(c => c.id === category.id);
                        const isSelected = selectedRows.has(category.id);
                        const isCellSelected = selectedCell?.categoryIndex === categoryIdx;
                        return (
                          <Fragment key={category.id}>
                            <TableRow
                              className={cn(
                                'group',
                                isCellSelected && 'bg-primary/5',
                                isSelected && 'bg-primary/10',
                              )}
                            >
                              <TableCell className={cn(
                                'p-2 w-[40px] sticky left-0 z-10',
                                isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'
                              )}>
                                <div className="flex items-center h-9">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleRowSelection(category.id)}
                                    aria-label={`Select ${category.name}`}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className={cn(
                                'p-2 w-[200px] sticky left-[40px] z-10 border-r',
                                isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'
                              )}>
                                <Popover
                                    open={openCategoryPopover === categoryIdx}
                                    onOpenChange={(open) => setOpenCategoryPopover(open ? categoryIdx : null)}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openCategoryPopover === categoryIdx}
                                        className={cn(
                                          "w-full justify-between h-9",
                                          !category.name && "text-muted-foreground"
                                        )}
                                      >
                                        <span className="truncate">
                                          {category.name
                                            ? (duplicateCategoryNames.has(category.name) && category.code
                                                ? `${category.name} (${category.code})`
                                                : category.name)
                                            : "Select category"}
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                      <Command shouldFilter={true} filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                                        <CommandInput placeholder="Search categories..." className="h-9" />
                                        <CommandList>
                                          <CommandEmpty>No category found.</CommandEmpty>
                                          {(() => {
                                            const usedIds = displayCategories
                                              .filter(c => c.id !== category.id)
                                              .map(c => c.globalCategoryId)
                                              .filter(Boolean);
                                            const available = availableGlobalCategories.filter(gc => !usedIds.includes(gc.id) && gc.id !== cascadeTargetCategoryId);
                                            if (category.globalCategoryId && !available.some(gc => gc.id === category.globalCategoryId)) {
                                              const current = availableGlobalCategories.find(gc => gc.id === category.globalCategoryId);
                                              if (current) available.unshift(current);
                                            }
                                            const groups = COST_CATEGORY_TYPE_ORDER
                                              .map(type => ({
                                                type,
                                                label: COST_CATEGORY_TYPE_LABELS[type] || type,
                                                items: available.filter(gc => gc.type === type),
                                              }))
                                              .filter(g => g.items.length > 0);
                                            return groups.map((group, groupIndex) => (
                                              <div key={group.type}>
                                                {groupIndex > 0 && <CommandSeparator />}
                                                <CommandGroup
                                                  heading={group.label}
                                                  className="**:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-wider **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:border-b **:[[cmdk-group-heading]]:border-border **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:mb-1"
                                                >
                                                  {group.items.map(gc => (
                                                    <CommandItem
                                                      key={gc.id}
                                                      value={`${gc.name} ${gc.code ?? ''}`}
                                                      onSelect={() => {
                                                        updateCategorySelection(categoryIdx, String(gc.id));
                                                        setOpenCategoryPopover(null);
                                                      }}
                                                      className="pl-3"
                                                    >
                                                      <div className="flex-1 min-w-0">
                                                        {gc.name}
                                                        {gc.code && (
                                                          <span className="ml-1.5 text-xs text-muted-foreground font-mono">{gc.code}</span>
                                                        )}
                                                      </div>
                                                      <Check
                                                        className={cn(
                                                          "h-4 w-4 shrink-0",
                                                          category.globalCategoryId === gc.id ? "opacity-100" : "opacity-0"
                                                        )}
                                                      />
                                                    </CommandItem>
                                                  ))}
                                                </CommandGroup>
                                              </div>
                                            ));
                                          })()}
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-background"></TableCell>
                        <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r font-semibold bg-background">Total</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* MIDDLE SECTION: Monthly Columns (Scrollable) */}
                <div 
                  className="flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)',
                  }}
                >
                  <Table 
                    className="border-collapse"
                    style={{
                      // Make table width match the number of months (100px per month)
                      width: `${yearMonths.length * 100}px`,
                      minWidth: '100%'
                    }}
                  >
                    <TableHeader>
                      <TableRow>
                        {yearMonths.map((monthData) => (
                          <TableHead key={`${monthData.year}-${monthData.month}`} className="w-[100px] text-center">
                            {MONTHS[monthData.month]}/{String(monthData.year).slice(-2)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* ── Synthetic Labour group monthly cells ── */}
                      {showSyntheticLabour && (
                        <Fragment key="labour-group-middle">
                          <TableRow className="bg-muted/40">
                            {visibleMonths.map((monthIndex) => (
                              <TableCell key={monthIndex} className="p-2 w-[100px] bg-muted/40">
                                <Input
                                  value={formatCurrency(labourGroupMonthly[monthIndex] ?? 0)}
                                  readOnly
                                  disabled
                                  className="w-full h-9 px-2 text-sm text-center bg-muted/50 cursor-not-allowed text-muted-foreground"
                                  title="Labour cost is calculated from Resource Budget (hours × rate)"
                                />
                              </TableCell>
                            ))}
                          </TableRow>

                          {/* Role monthly cost child rows */}
                          {isLabourGroupExpanded && labourRoles.map(role => (
                            <TableRow key={`labour-role-middle-${role.id}`} className="bg-muted/10 h-[41px]">
                              {visibleMonths.map((projectMonthIndex) => {
                                const ym = yearMonths[projectMonthIndex];
                                const mk = ym ? `${ym.year}-${String(ym.month + 1).padStart(2, '0')}` : '';
                                const hours = role.monthlyHours?.[mk] || 0;
                                const effectiveRate = getEffectiveCostRate(role);
                                const cost = hours * effectiveRate;
                                return (
                                  <TableCell key={projectMonthIndex} className="p-2 w-[100px] bg-muted/10 h-[41px]">
                                    <Input
                                      value={hours > 0 ? formatCurrency(cost) : '-'}
                                      readOnly
                                      className="w-full h-9 px-2 text-sm text-center bg-transparent text-muted-foreground cursor-default border-transparent"
                                      title={hours > 0 ? `${hours}h × ${formatCurrency(effectiveRate)} = ${formatCurrency(cost)}` : 'No hours allocated'}
                                    />
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </Fragment>
                      )}

                      {/* ── Non-labour category monthly cells ── */}
                      {displayCategories.map((category) => {
                        const categoryIdx = categories.findIndex(c => c.id === category.id);
                        const isSelected = selectedCell?.categoryIndex === categoryIdx && selectedCell?.monthIndex !== null;

                        return (
                          <Fragment key={category.id}>
                            <TableRow>
                              {visibleMonths.map((monthIndex) => {
                                const existingBudgets = category.monthlyBudgets || [];
                                const monthlyBudgets = Array(totalMonths).fill(0).map((_, i) => existingBudgets[i] || 0);
                                const isCellSelected = selectedCell?.categoryIndex === categoryIdx && selectedCell?.monthIndex === monthIndex;

                                return (
                                  <TableCell key={monthIndex} className="p-2 w-[100px]">
                                    <NumberInput
                                      value={monthlyBudgets[monthIndex]}
                                      onChange={(value) => updateMonthlyBudget(categoryIdx, monthIndex, value)}
                                      onCommit={onCellCommit}
                                      maxValue={999999.99}
                                      onFocus={() => setSelectedCell({ categoryIndex: categoryIdx, monthIndex })}
                                      className={cn(
                                        "w-full h-9 px-2 text-sm text-center",
                                        isCellSelected && "ring-2 ring-ring"
                                      )}
                                    />
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {visibleMonths.map((monthIndex) => {
                          const monthTotal =
                            (labourGroupMonthly[monthIndex] ?? 0) +
                            displayCategories.reduce((sum, category) => {
                              const existingBudgets = category.monthlyBudgets || [];
                              return sum + (existingBudgets[monthIndex] || 0);
                            }, 0);
                          return (
                            <TableCell key={monthIndex} className="p-2 w-[100px]">
                              <Input
                                value={formatCurrency(monthTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted cursor-default"
                                title={formatCurrency(monthTotal)}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* RIGHT SECTION: Project Total (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-l-2">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px] text-center font-semibold bg-primary/10">Project Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* ── Synthetic Labour group total ── */}
                      {showSyntheticLabour && (
                        <Fragment key="labour-group-right">
                          <TableRow className="bg-muted/40">
                            <TableCell className="p-2 w-[120px] bg-muted/40">
                              <Input
                                value={formatCurrency(labourGroupTotal)}
                                readOnly
                                disabled
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted/50 text-muted-foreground cursor-not-allowed"
                                title="Labour cost total from Resource Budget"
                              />
                            </TableCell>
                          </TableRow>

                          {/* Role total child rows */}
                          {isLabourGroupExpanded && labourRoles.map(role => {
                            const roleCostRate = getEffectiveCostRate(role);
                            const roleTotal = Object.values(role.monthlyHours || {}).reduce((sum, hours) => sum + (hours * roleCostRate), 0);
                            return (
                              <TableRow key={`labour-role-right-${role.id}`} className="bg-muted/10 h-[41px]">
                                <TableCell className="p-2 w-[120px] bg-muted/10 h-[41px]">
                                  <Input
                                    value={formatCurrency(roleTotal)}
                                    readOnly
                                    className="w-full h-9 px-2 text-sm text-center bg-transparent text-muted-foreground cursor-default border-transparent"
                                    title={`Total cost for ${role.role}: ${formatCurrency(roleTotal)}`}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      )}

                      {/* ── Non-labour category totals ── */}
                      {displayCategories.map((category) => {
                        const categoryTotal = category.monthlyBudgets.reduce((sum, val) => sum + val, 0);
                        return (
                          <TableRow key={category.id}>
                            <TableCell className="p-2 w-[120px]">
                              <Input
                                value={formatCurrency(categoryTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
                                title={formatCurrency(categoryTotal)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[120px] bg-background">
                          <Input
                            value={formatCurrency(
                              labourGroupTotal +
                              displayCategories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0)
                            )}
                            readOnly
                            className="w-full h-9 px-2 text-sm text-center font-bold bg-primary/20 cursor-default"
                          />
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${formatCurrency(patternSuggestion.value)}?`}
          details={`You've entered ${formatCurrency(patternSuggestion.value)} for 3 consecutive months. Would you like to fill ${MONTHS[yearMonths[patternSuggestion.fromMonth]?.month]} through ${MONTHS[yearMonths[patternSuggestion.toMonth]?.month]} with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        onApply={handleFillApply}
        sourceMonth={fillSource?.monthIndex ?? 0}
        totalMonths={totalMonths}
        sourceValue={fillSource?.value ?? 0}
        direction="right"
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
        budgetType="cost"
      />

      {/* Shift Budget Sheet */}
      <ShiftBudgetSheet
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        onShift={handleShiftBudget}
        totalMonths={totalMonths}
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
        roleName={shiftSource?.categoryName ?? ''}
        monthlyHours={shiftSource ? categories[shiftSource.categoryIndex]?.monthlyBudgets ?? [] : []}
        selectedMonthIndex={selectedCell?.monthIndex ?? undefined}
        budgetType="cost"
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteCategory()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete Category with Costs</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                The category <strong>"{deleteAttempt?.category.name}"</strong> cannot be deleted because it has budget or actual costs allocated.
              </p>
              <p className="text-sm">
                To remove this category from your project:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                <li>Clear all monthly budget values (set them to zero)</li>
                <li>Ensure there are no actual costs recorded</li>
                <li>Then try deleting the category again</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                This prevents accidental deletion of categories with financial data.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={cancelDeleteCategory}>
              I Understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

// ── CostCentreBudgetView ─────────────────────────────────────────────────────
// Renders cost centres as rows in a sticky table matching the Sales Budget CC
// pattern. Each cost centre has ONE monthly budget value per month (no category
// sub-rows). Parent CCs in structure view auto-sum from leaf children (read-only).
//
// Budget values are persisted to localStorage keyed by projectId + ccCode.
// ─────────────────────────────────────────────────────────────────────────────

// Simple per-CC monthly budget storage with API persistence
const CC_COST_BUDGET_STORAGE_KEY_V2 = (projectId: string, ccCode: string) =>
  `cc-cost-budget-v2-${projectId}-${ccCode}`;

function loadCcCostBudget(projectId: string, ccCode: string): Record<string, number> {
  if (getDataMode() === 'live') return {};
  try {
    const raw = localStorage.getItem(CC_COST_BUDGET_STORAGE_KEY_V2(projectId, ccCode));
    if (raw) return JSON.parse(raw) as Record<string, number>;
  } catch { /* ignore */ }
  return {};
}

function saveCcCostBudget(projectId: string, ccCode: string, monthly: Record<string, number>) {
  if (getDataMode() === 'live') return;
  // Write-through to localStorage for immediate UI consistency
  try {
    localStorage.setItem(CC_COST_BUDGET_STORAGE_KEY_V2(projectId, ccCode), JSON.stringify(monthly));
  } catch { /* ignore */ }
}

// Debounced API persist for cost centre budgets
const ccCostBudgetPendingSaves: Record<string, {
  timer: ReturnType<typeof setTimeout>;
  save: () => Promise<void>;
}> = {};

function persistCcCostBudgetToApi(costCentreId: number, ccCode: string, monthly: Record<string, number>) {
  const timerKey = `cost-${ccCode}`;
  if (ccCostBudgetPendingSaves[timerKey]) {
    clearTimeout(ccCostBudgetPendingSaves[timerKey].timer);
  }
  const save = () => upsertCostCentreBudget(costCentreId, 'cost', monthly);
  ccCostBudgetPendingSaves[timerKey] = {
    timer: setTimeout(() => {
      save().catch((err) => {
        console.error(`[CostBudget] Failed to persist budget for CC ${ccCode}:`, err);
      });
      delete ccCostBudgetPendingSaves[timerKey];
    }, 800),
    save,
  };
}

export async function flushPendingCcCostBudgetSaves(): Promise<void> {
  const pending = Object.entries(ccCostBudgetPendingSaves);
  if (pending.length === 0) return;
  for (const [, { timer }] of pending) {
    clearTimeout(timer);
  }
  await Promise.all(
    pending.map(([key, { save }]) => {
      delete ccCostBudgetPendingSaves[key];
      return save().catch((err) => {
        console.error('[CostBudget] Failed to flush pending CC budget save:', err);
      });
    })
  );
}

interface CostCentreBudgetViewProps {
  costCentres: CostCentre[];
  hierarchicalCostCentres: HierarchicalCostCentre[];
  isStructureView: boolean;
  projectId: string;
  yearMonths: { year: number; month: number }[];
  monthIndexMap: { globalIndex: number; year: number; month: number; yearMonthKey: string }[];
  totalMonths: number;
  onChange: (changes: CostBudget) => void;
  onCellCommit?: () => void;
  costBudget: CostBudget;
}

function CostCentreBudgetView({
  costCentres,
  hierarchicalCostCentres,
  isStructureView,
  projectId,
  yearMonths,
  onChange,
  onCellCommit,
  costBudget,
}: CostCentreBudgetViewProps) {

  // ── Build ordered flat list of CC nodes ────────────────────────────────
  const flatRows = useMemo<FlatCbNode[]>(() => {
    if (isStructureView && hierarchicalCostCentres.length > 0) {
      return flattenCbHierarchy(buildCbTree(hierarchicalCostCentres));
    }
    return costCentres.map(cc => ({ ...cc, level: 0, hasChildren: false, children: [] }));
  }, [isStructureView, hierarchicalCostCentres, costCentres]);

  // ── Per-CC monthly budgets: ccCode → Record<"YYYY-MM", number> ─────────
  // When the parent passes costCentreBudgets (e.g. via undo/redo), use those
  // values instead of localStorage so the restored state is accurate.
  const [ccBudgets, setCcBudgets] = useState<Record<string, Record<string, number>>>(() => {
    if (costBudget.costCentreBudgets && Object.keys(costBudget.costCentreBudgets).length > 0) {
      return { ...costBudget.costCentreBudgets };
    }
    const initial: Record<string, Record<string, number>> = {};
    for (const cc of flatRows) {
      initial[cc.code] = loadCcCostBudget(projectId, cc.code);
    }
    return initial;
  });

  // Fetch CC cost budgets from API on mount and update state
  const apiFetchedRef = useRef(false);
  useEffect(() => {
    if (apiFetchedRef.current) return;
    apiFetchedRef.current = true;
    const numericPid = parseInt(projectId, 10);
    if (!numericPid) return;
    fetchCostCentreBudgetsByProject(numericPid, 'cost')
      .then((entries) => {
        if (!entries || entries.length === 0) return;
        skipNextOnChange.current = true;
        setCcBudgets((prev) => {
          const updated = { ...prev };
          for (const entry of entries) {
            const cc = costCentres.find(c => c.id === entry.costCentreId);
            const code = entry.costCentreCode || cc?.code;
            if (code && entry.monthlyBudgets) {
              const monthly = typeof entry.monthlyBudgets === 'string'
                ? JSON.parse(entry.monthlyBudgets) as Record<string, number>
                : entry.monthlyBudgets;
              updated[code] = monthly;
              saveCcCostBudget(projectId, code, monthly);
            }
          }
          return updated;
        });
      })
      .catch((err) => {
        console.error('[CostBudget] Failed to fetch CC budgets from API:', err);
      });
  }, [projectId, costCentres]);

  // ── Expand/collapse for structure-view parent CCs ──────────────────────
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(
    () => new Set(flatRows.filter(r => r.hasChildren).map(r => r.id))
  );

  const toggleNode = (id: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAllNodes = () =>
    setExpandedNodes(new Set(flatRows.filter(r => r.hasChildren).map(r => r.id)));

  const collapseAllNodes = () => setExpandedNodes(new Set());

  // ── Cell selection ──────────────────────────────────────────────────────
  const [selectedCell, setSelectedCell] = useState<{ ccCode: string; monthKey: string } | null>(null);

  // ── Fill state ──────────────────────────────────────────────────────────
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ ccCode: string; monthIndex: number; value: number } | null>(null);

  // ── Shift state ─────────────────────────────────────────────────────────
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ ccCode: string; ccName: string } | null>(null);

  // ── Pattern detection (AI suggestion) ──────────────────────────────────
  const [patternSuggestion, setPatternSuggestion] = useState<{
    ccCode: string;
    value: number;
    fromMonthIndex: number;
    toMonthIndex: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  const detectPattern = (ccCode: string, monthIndex: number, value: number, currentBudgets: Record<string, number>) => {
    const suggestionKey = `pattern-${ccCode}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    if (monthIndex >= 2 && value > 0) {
      const prev2Key = yearMonths[monthIndex - 2];
      const prev1Key = yearMonths[monthIndex - 1];
      if (!prev2Key || !prev1Key) return;
      const prev2 = currentBudgets[`${prev2Key.year}-${String(prev2Key.month + 1).padStart(2, '0')}`] ?? 0;
      const prev1 = currentBudgets[`${prev1Key.year}-${String(prev1Key.month + 1).padStart(2, '0')}`] ?? 0;
      if (prev2 === value && prev1 === value && monthIndex < yearMonths.length - 1) {
        setPatternSuggestion({ ccCode, value, fromMonthIndex: monthIndex + 1, toMonthIndex: yearMonths.length - 1 });
      }
    }
  };

  const updateCell = (ccCode: string, monthKey: string, value: number) => {
    setCcBudgets(prev => {
      const updated = { ...prev, [ccCode]: { ...(prev[ccCode] ?? {}), [monthKey]: value } };
      const monthIndex = yearMonths.findIndex(
        (ym, i) => `${ym.year}-${String(ym.month + 1).padStart(2, '0')}` === monthKey
      );
      if (monthIndex !== -1) detectPattern(ccCode, monthIndex, value, updated[ccCode]);
      return updated;
    });
  };

  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    const { ccCode, value, fromMonthIndex, toMonthIndex } = patternSuggestion;
    setCcBudgets(prev => {
      const existing = { ...(prev[ccCode] ?? {}) };
      for (let i = fromMonthIndex; i <= toMonthIndex; i++) {
        const ym = yearMonths[i];
        if (ym) existing[`${ym.year}-${String(ym.month + 1).padStart(2, '0')}`] = value;
      }
      return { ...prev, [ccCode]: existing };
    });
    setPatternSuggestion(null);
    onCellCommit?.();
  };

  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      setDismissedSuggestions(prev => new Set(prev).add(`pattern-${patternSuggestion.ccCode}`));
    }
    setPatternSuggestion(null);
  };

  // ── Fill handlers ───────────────────────────────────────────────────────
  const handleFillClick = (ccCode: string, monthIndex: number) => {
    const ym = yearMonths[monthIndex];
    const monthKey = ym ? `${ym.year}-${String(ym.month + 1).padStart(2, '0')}` : '';
    const value = ccBudgets[ccCode]?.[monthKey] ?? 0;
    setFillSource({ ccCode, monthIndex, value });
    setFillDialogOpen(true);
  };

  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;
    const { ccCode, monthIndex, value } = fillSource;
    setCcBudgets(prev => {
      const existing = { ...(prev[ccCode] ?? {}) };
      const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
      const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
      const count = Math.abs(endMonth - startMonth) + 1;
      if (fillType === 'copy') {
        for (let i = startMonth; i <= endMonth; i++) {
          const ym = yearMonths[i];
          if (ym) existing[`${ym.year}-${String(ym.month + 1).padStart(2, '0')}`] = value;
        }
      } else if (fillType === 'distribute') {
        const perMonth = Math.floor((value / count) * 100) / 100;
        const remainder = value - perMonth * count;
        for (let i = startMonth; i <= endMonth; i++) {
          const ym = yearMonths[i];
          if (ym) existing[`${ym.year}-${String(ym.month + 1).padStart(2, '0')}`] = i === endMonth ? perMonth + remainder : perMonth;
        }
      }
      return { ...prev, [ccCode]: existing };
    });
    setFillDialogOpen(false);
    setFillSource(null);
    onCellCommit?.();
  };

  // ── Shift handlers ──────────────────────────────────────────────────────
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;
    const { ccCode } = shiftSource;
    setCcBudgets(prev => {
      const existing = { ...(prev[ccCode] ?? {}) };
      const shiftAmount = destinationStart - sourceFrom;
      const original = { ...existing };
      for (let i = sourceFrom; i <= sourceTo; i++) {
        const dest = i + shiftAmount;
        if (dest >= 0 && dest < yearMonths.length) {
          const destYm = yearMonths[dest];
          const srcYm = yearMonths[i];
          if (destYm && srcYm) {
            existing[`${destYm.year}-${String(destYm.month + 1).padStart(2, '0')}`] =
              original[`${srcYm.year}-${String(srcYm.month + 1).padStart(2, '0')}`] ?? 0;
          }
        }
      }
      if (clearSource) {
        const destEnd = sourceTo + shiftAmount;
        for (let i = sourceFrom; i <= sourceTo; i++) {
          if (i < destinationStart || i > destEnd) {
            const srcYm = yearMonths[i];
            if (srcYm) existing[`${srcYm.year}-${String(srcYm.month + 1).padStart(2, '0')}`] = 0;
          }
        }
      }
      return { ...prev, [ccCode]: existing };
    });
    setShiftDialogOpen(false);
    setShiftSource(null);
    onCellCommit?.();
  };

  // ── Propagate changes on every edit (API persist deferred to Save Budget) ──
  const isInitialMount = useRef(true);
  const skipNextOnChange = useRef(false);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    if (skipNextOnChange.current) { skipNextOnChange.current = false; return; }
    // Update localStorage cache for immediate UI consistency on re-open
    for (const [code, monthly] of Object.entries(ccBudgets)) {
      saveCcCostBudget(projectId, code, monthly);
    }
    // Build pending CC budget entries for the save flow
    const pendingCcBudgets = Object.entries(ccBudgets)
      .map(([code, monthly]) => {
        const cc = costCentres.find(c => c.code === code);
        return cc ? { costCentreId: cc.id, budgetType: 'cost' as const, monthlyBudgets: monthly } : null;
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);
    // Propagate totals
    const totalBudget = Object.values(ccBudgets).reduce(
      (sum, monthly) => sum + Object.values(monthly).reduce((s, v) => s + v, 0),
      0
    );
    onChange({
      ...costBudget,
      costCentreBudgets: ccBudgets,
      totals: {
        budget: totalBudget,
        actual: costBudget.totals?.actual ?? 0,
        committed: costBudget.totals?.committed ?? 0,
        available: totalBudget - (costBudget.totals?.actual ?? 0) - (costBudget.totals?.committed ?? 0),
        progressPercent: totalBudget > 0 ? ((costBudget.totals?.actual ?? 0) / totalBudget) * 100 : 0,
      },
      pendingCcBudgets,
    });
  }, [ccBudgets]);

  // ── Derived values ──────────────────────────────────────────────────────
  const monthKey = (ym: { year: number; month: number }) =>
    `${ym.year}-${String(ym.month + 1).padStart(2, '0')}`;

  const leafRows = isStructureView ? flatRows.filter(r => !r.hasChildren) : flatRows;

  // For a leaf CC: sum its monthly values
  const leafTotal = (ccCode: string) =>
    yearMonths.reduce((s, ym) => s + (ccBudgets[ccCode]?.[monthKey(ym)] ?? 0), 0);

  const leafMonthTotal = (ccCode: string, ym: { year: number; month: number }) =>
    ccBudgets[ccCode]?.[monthKey(ym)] ?? 0;

  // For a parent CC in structure view: recursively sum all leaf descendants
  const getAllLeafDescendants = useCallback((parentId: number): FlatCbNode[] => {
    const directChildren = flatRows.filter(r => r.parentId === parentId);
    const result: FlatCbNode[] = [];
    for (const child of directChildren) {
      if (child.hasChildren) result.push(...getAllLeafDescendants(child.id));
      else result.push(child);
    }
    return result;
  }, [flatRows]);

  const parentMonthTotal = useCallback((parentId: number, ym: { year: number; month: number }) =>
    getAllLeafDescendants(parentId).reduce((s, leaf) => s + leafMonthTotal(leaf.code, ym), 0),
  [getAllLeafDescendants, ccBudgets]);

  const parentTotal = useCallback((parentId: number) =>
    getAllLeafDescendants(parentId).reduce((s, leaf) => s + leafTotal(leaf.code), 0),
  [getAllLeafDescendants, ccBudgets]);

  // Column totals (leaf rows only to avoid double-counting)
  const colTotals: Record<string, number> = {};
  for (const ym of yearMonths) {
    const k = monthKey(ym);
    colTotals[k] = leafRows.reduce((s, cc) => s + (ccBudgets[cc.code]?.[k] ?? 0), 0);
  }
  const grandTotal = Object.values(colTotals).reduce((s, v) => s + v, 0);

  // Visible rows (structure view: collapse children of collapsed parents)
  const visibleRows = useMemo(() => {
    if (!isStructureView) return flatRows;
    const parentMap = new Map<number, number | null>();
    for (const row of flatRows) parentMap.set(row.id, row.parentId ?? null);
    return flatRows.filter(row => {
      let pid = row.parentId ?? null;
      while (pid !== null && pid !== undefined) {
        if (!expandedNodes.has(pid)) return false;
        pid = parentMap.get(pid) ?? null;
      }
      return true;
    });
  }, [flatRows, expandedNodes, isStructureView]);

  // Toolbar button states
  const selectedMonthIndex = selectedCell
    ? yearMonths.findIndex((ym) => monthKey(ym) === selectedCell.monthKey)
    : -1;
  const selectedValue = selectedCell
    ? (ccBudgets[selectedCell.ccCode]?.[selectedCell.monthKey] ?? 0)
    : 0;
  const fillEnabled = selectedCell !== null && selectedValue > 0 && selectedMonthIndex < yearMonths.length - 1;
  const shiftEnabled = selectedCell !== null && selectedValue > 0;

  if (flatRows.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground space-y-2">
        <p className="font-medium">No cost centres found for this project.</p>
        <p className="text-sm">Cost centres must be created on the Cost Centres tab before budgets can be entered in Cost Centre mode.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 -mx-6">
      <div className="overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Cost Budget by Cost Centre</h2>
            {isStructureView && (
              <>
                <Button size="sm" variant="outline" onClick={expandAllNodes}>
                  Expand All
                </Button>
                <Button size="sm" variant="outline" onClick={collapseAllNodes}>
                  Collapse All
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!fillEnabled}
              title="Fill right from selected cell"
              onClick={() => {
                if (selectedCell) handleFillClick(selectedCell.ccCode, selectedMonthIndex);
              }}
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!shiftEnabled}
              title="Shift budget between months"
              onClick={() => {
                if (selectedCell) {
                  const cc = flatRows.find(r => r.code === selectedCell.ccCode);
                  setShiftSource({ ccCode: selectedCell.ccCode, ccName: cc?.name ?? selectedCell.ccCode });
                  setShiftDialogOpen(true);
                }
              }}
            >
              <MoveHorizontal className="h-4 w-4 mr-2" />
              Shift
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="sticky left-0 z-20 bg-muted/50 min-w-[90px] border-r text-xs font-semibold">Code</TableHead>
                <TableHead className="sticky left-[90px] z-20 bg-muted/50 min-w-[220px] border-r text-xs font-semibold">Name</TableHead>
                {yearMonths.map((ym, idx) => (
                  <TableHead key={idx} className="min-w-[110px] text-right text-xs font-semibold px-2">
                    {MONTHS[ym.month]} {ym.year}
                  </TableHead>
                ))}
                <TableHead className="min-w-[110px] text-right text-xs font-semibold px-2 border-l">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((cc) => {
                const isParent = cc.hasChildren;
                const isExpanded = expandedNodes.has(cc.id);
                const indentWidth = cc.level * 24;
                const rowBg = isParent ? 'bg-muted/50' : 'bg-background';
                const rowTotal = isParent ? parentTotal(cc.id) : leafTotal(cc.code);

                return (
                  <TableRow key={`${cc.id}-${cc.code}`} className={cn('group', isParent && 'border-t border-b border-border/60')}>
                    {/* Code column */}
                    <TableCell className={cn('sticky left-0 z-10 border-r px-1 py-2 text-sm', rowBg)}>
                      <div className="flex items-center gap-1">
                        {isParent ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-muted shrink-0"
                            onClick={() => toggleNode(cc.id)}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        ) : (
                          <div className="w-6 shrink-0" />
                        )}
                        <span className={cn('font-mono text-xs', isParent ? 'font-bold text-foreground' : 'text-muted-foreground')}>{cc.code}</span>
                      </div>
                    </TableCell>
                    {/* Name column */}
                    <TableCell className={cn('sticky left-[90px] z-10 border-r py-2 text-sm', rowBg)}>
                      <div className="flex items-center gap-1" style={{ paddingLeft: `${indentWidth}px` }}>
                        {isParent
                          ? isExpanded
                            ? <FolderOpen className="h-4 w-4 text-info-500 shrink-0" />
                            : <Folder className="h-4 w-4 text-info-500 shrink-0" />
                          : null}
                        <span className={cn('font-medium truncate', isParent ? 'text-foreground' : 'text-foreground/80')}>
                          {cc.name}
                        </span>
                        {cc.type && !isParent && (
                          <span className="ml-1 text-xs text-muted-foreground">({cc.type})</span>
                        )}
                      </div>
                    </TableCell>
                    {/* Monthly cells */}
                    {yearMonths.map((ym, idx) => {
                      const k = monthKey(ym);
                      return (
                        <TableCell key={idx} className="px-1 py-1 text-right">
                          {isParent ? (
                            <span className="text-sm tabular-nums text-muted-foreground px-3 block text-right">
                              {formatCurrency(parentMonthTotal(cc.id, ym))}
                            </span>
                          ) : (
                            <NumberInput
                              value={ccBudgets[cc.code]?.[k] ?? 0}
                              onChange={v => updateCell(cc.code, k, v)}
                              onCommit={onCellCommit}
                              onFocus={() => setSelectedCell({ ccCode: cc.code, monthKey: k })}
                              className={cn(
                                'h-8 text-sm text-right tabular-nums w-full min-w-[100px]',
                                selectedCell?.ccCode === cc.code && selectedCell?.monthKey === k && 'ring-2 ring-ring'
                              )}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                    {/* Row total */}
                    <TableCell className={cn('px-3 py-2 text-right text-sm font-semibold tabular-nums border-l', isParent ? 'text-foreground' : 'text-muted-foreground')}>
                      {formatCurrency(rowTotal)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-muted/50 font-semibold">
                <TableCell className="sticky left-0 z-10 bg-muted/50 border-r px-3 py-2 text-xs" colSpan={2}>
                  Total
                </TableCell>
                {yearMonths.map((ym, idx) => (
                  <TableCell key={idx} className="px-3 py-2 text-right text-sm tabular-nums">
                    {formatCurrency(colTotals[monthKey(ym)] ?? 0)}
                  </TableCell>
                ))}
                <TableCell className="px-3 py-2 text-right text-sm tabular-nums border-l">
                  {formatCurrency(grandTotal)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${formatCurrency(patternSuggestion.value)}?`}
          details={`You've entered ${formatCurrency(patternSuggestion.value)} for 3 consecutive months. Would you like to fill the remaining months with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        onApply={handleFillApply}
        sourceMonth={fillSource?.monthIndex ?? 0}
        totalMonths={yearMonths.length}
        sourceValue={fillSource?.value ?? 0}
        direction="right"
        monthLabels={yearMonths.map(({ year, month }) => `${MONTHS[month]} ${year}`)}
      />

      {/* Shift Budget Sheet */}
      <ShiftBudgetSheet
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        onShift={handleShiftBudget}
        totalMonths={yearMonths.length}
        monthLabels={yearMonths.map(({ year, month }) => `${MONTHS[month]} ${year}`)}
        roleName={shiftSource?.ccName ?? ''}
        monthlyHours={shiftSource ? yearMonths.map(ym => ccBudgets[shiftSource.ccCode]?.[monthKey(ym)] ?? 0) : []}
        selectedMonthIndex={selectedMonthIndex >= 0 ? selectedMonthIndex : undefined}
      />
    </div>
  );
}


